/**
 * ProgressPage component - Progress tracking and pattern analysis.
 *
 * Material Design themed with elegant cards and consistent styling matching HomePage.
 */

import React, { useState, useMemo } from 'react';
import { useTimeline, useTrends, usePatterns, useHRVTrends, useSleepStageTrends, useCorrelations, useOptimalRanges } from '../services/api';
import Timeline from '../components/progress/Timeline';
import TrendCharts from '../components/progress/TrendCharts';
import PatternInsights from '../components/progress/PatternInsights';
import LoadingSkeleton from '../components/progress/LoadingSkeleton';
import HRVTrendChart from '../components/progress/HRVTrendChart';
import SleepStageTrendChart from '../components/progress/SleepStageTrendChart';
import DateRangeSelector from '../components/progress/DateRangeSelector';
import ComparisonView from '../components/progress/ComparisonView';
import ExportButton from '../components/progress/ExportButton';

const DATE_RANGE_OPTIONS = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
  { value: 90, label: '90 Days' }
];

const ProgressPage: React.FC = () => {
  const [dateRange, setDateRange] = useState(30);
  const [comparisonMode, setComparisonMode] = useState(false);

  const {
    data: timelineData,
    isLoading: timelineLoading,
    isError: timelineError
  } = useTimeline(dateRange);

  const {
    data: trendsData,
    isLoading: trendsLoading,
    isError: trendsError
  } = useTrends(dateRange);

  const {
    data: patternsData,
    isLoading: patternsLoading,
    isError: patternsError
  } = usePatterns();

  const {
    data: hrvTrendsData,
    isLoading: hrvTrendsLoading,
    isError: hrvTrendsError
  } = useHRVTrends(dateRange);

  const {
    data: sleepStageTrendsData,
    isLoading: sleepStageTrendsLoading,
    isError: sleepStageTrendsError
  } = useSleepStageTrends(dateRange);

  // Fetch correlations (only when we have 30+ days)
  const {
    data: correlationsData
  } = useCorrelations(dateRange >= 30 ? dateRange : 30);

  // Fetch optimal ranges (only when we have 30+ days)
  const {
    data: optimalRangesData
  } = useOptimalRanges(dateRange >= 30 ? dateRange : 30, 'energy_level');

  // Fetch previous period data for comparison (only when comparison mode is enabled)
  const {
    data: previousTrendsData,
    isLoading: previousTrendsLoading
  } = useTrends(dateRange);

  // Calculate comparison metrics
  const comparisonMetrics = useMemo(() => {
    if (!comparisonMode || !trendsData || !previousTrendsData) return [];

    // Split data into current and previous periods
    const splitIndex = Math.floor(trendsData.sleep_quality?.length / 2) || 0;

    const calculateMetric = (
      name: string,
      icon: string,
      unit: string,
      currentData: any[],
      previousData: any[]
    ) => {
      if (!currentData?.length || !previousData?.length) return null;

      const currentValues = currentData.slice(splitIndex).map((d: any) => d.value);
      const previousValues = previousData.slice(0, splitIndex).map((d: any) => d.value);

      if (!currentValues.length || !previousValues.length) return null;

      const currentAvg = currentValues.reduce((a: number, b: number) => a + b, 0) / currentValues.length;
      const previousAvg = previousValues.reduce((a: number, b: number) => a + b, 0) / previousValues.length;
      const percentageChange = ((currentAvg - previousAvg) / previousAvg) * 100;

      const getTrendDirection = (change: number): 'improving' | 'declining' | 'stable' => {
        if (Math.abs(change) < 5) return 'stable';
        return change > 0 ? 'improving' : 'declining';
      };

      return {
        name,
        icon,
        unit,
        current: {
          label: `Current ${dateRange / 2} Days`,
          avg: currentAvg,
          min: Math.min(...currentValues),
          max: Math.max(...currentValues)
        },
        previous: {
          label: `Previous ${dateRange / 2} Days`,
          avg: previousAvg,
          min: Math.min(...previousValues),
          max: Math.max(...previousValues)
        },
        percentageChange,
        trendDirection: getTrendDirection(percentageChange)
      };
    };

    const metrics = [
      calculateMetric('Sleep Quality', '😴', '/ 5', trendsData.sleep_quality, previousTrendsData.sleep_quality),
      calculateMetric('Energy Level', '⚡', '/ 100', trendsData.energy_level, previousTrendsData.energy_level),
      calculateMetric('Mood', '😊', '/ 100', trendsData.mood, previousTrendsData.mood)
    ];

    return metrics.filter((m) => m !== null);
  }, [comparisonMode, trendsData, previousTrendsData, dateRange]);

  const isLoading = timelineLoading || trendsLoading || patternsLoading || hrvTrendsLoading || sleepStageTrendsLoading || (comparisonMode && previousTrendsLoading);

  // Loading state with branded skeleton
  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <LoadingSkeleton />
      </div>
    );
  }

  // Error state with helpful messaging
  if (timelineError || trendsError || patternsError) {
    return (
      <div className="max-w-6xl mx-auto animate-fade-in">
        <div className="card bg-gradient-to-br from-red-50 to-white border border-red-200 shadow-soft">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-2xl">⚠️</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-serif font-semibold text-red-900 mb-2">
                Unable to Load Progress Data
              </h3>
              <p className="text-red-800 mb-4 leading-relaxed">
                We're having trouble loading your progress data. This might be due to a temporary
                connection issue.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="px-5 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-200 shadow-soft hover:shadow-md"
                >
                  Try Again
                </button>
                <a
                  href="/"
                  className="px-5 py-2.5 bg-white border-2 border-red-600 text-red-700 font-semibold rounded-xl hover:bg-red-50 transition-all duration-200 text-center"
                >
                  Return to Home
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasData = timelineData?.timeline?.length > 0;

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <h1 className="text-4xl font-serif font-bold text-primary-800 mb-2">
            Your Progress
          </h1>
          <p className="text-lg text-neutral-600 leading-relaxed">
            Track your wellness journey and discover meaningful patterns over time
          </p>
        </div>

        {/* Controls: Date Range Selector, Comparison Mode, Export */}
        {hasData && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <DateRangeSelector
              selectedRange={dateRange}
              onChange={setDateRange}
              options={DATE_RANGE_OPTIONS}
            />

            {/* Comparison Mode Toggle */}
            <button
              onClick={() => setComparisonMode(!comparisonMode)}
              className={`px-5 py-2.5 rounded-xl font-semibold transition-all duration-200 shadow-soft ${
                comparisonMode
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 shadow-md'
                  : 'bg-white border-2 border-primary-300 text-primary-700 hover:border-primary-400 hover:bg-primary-50'
              }`}
            >
              {comparisonMode ? '📊 Viewing Comparison' : '📊 Compare Periods'}
            </button>

            {/* Export Button */}
            <ExportButton dateRange={dateRange} />
          </div>
        )}
      </div>

      {/* No Data State */}
      {!hasData && (
        <div className="card bg-gradient-to-br from-primary-50 to-white border border-primary-200 shadow-elegant text-center py-12 animate-slide-up">
          <div className="text-7xl mb-6">📊</div>
          <h2 className="text-2xl font-serif font-bold text-neutral-900 mb-3">
            Start Tracking Your Progress
          </h2>
          <p className="text-lg text-neutral-600 mb-6 max-w-lg mx-auto leading-relaxed">
            Complete your first check-in to begin visualizing your wellness journey and
            discovering patterns in your data.
          </p>
          <a
            href="/"
            className="inline-block px-8 py-3.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-soft hover:shadow-md"
          >
            Complete Your First Check-In
          </a>
        </div>
      )}

      {/* Summary Stats */}
      {hasData && timelineData?.aggregation && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 animate-slide-up">
          {/* Total Check-ins */}
          <div className="card bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 shadow-soft hover:shadow-md transition-shadow duration-200">
            <div className="text-sm text-neutral-600 font-medium mb-2">Total Check-ins</div>
            <div className="flex items-baseline space-x-2">
              <div className="text-4xl font-bold text-primary-700">
                {timelineData.aggregation.total_checkins}
              </div>
              <span className="text-neutral-500 text-sm font-medium">days</span>
            </div>
          </div>

          {/* Current Streak */}
          <div className="card bg-gradient-to-br from-accent-50 to-accent-100 border border-accent-200 shadow-soft hover:shadow-md transition-shadow duration-200">
            <div className="text-sm text-neutral-600 font-medium mb-2">Current Streak</div>
            <div className="flex items-baseline space-x-2">
              <div className="text-4xl font-bold text-accent-700">
                {timelineData.streaks?.current_streak || 0}
              </div>
              <span className="text-neutral-500 text-sm font-medium">days</span>
            </div>
            {(timelineData.streaks?.current_streak || 0) >= 7 && (
              <div className="mt-2 text-xs text-accent-600 font-medium">
                🔥 Great consistency!
              </div>
            )}
          </div>

          {/* Avg Sleep Quality */}
          <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 shadow-soft hover:shadow-md transition-shadow duration-200">
            <div className="text-sm text-neutral-600 font-medium mb-2">Avg Sleep Quality</div>
            <div className="flex items-baseline space-x-2">
              <div className="text-4xl font-bold text-blue-700">
                {timelineData.averages?.avg_sleep_quality?.toFixed(1) || 'N/A'}
              </div>
              <span className="text-neutral-500 text-sm font-medium">/ 5</span>
            </div>
          </div>

          {/* Most Common State */}
          <div className="card bg-gradient-to-br from-green-50 to-green-100 border border-green-200 shadow-soft hover:shadow-md transition-shadow duration-200">
            <div className="text-sm text-neutral-600 font-medium mb-2">Most Common State</div>
            <div className="text-lg font-semibold text-green-800 capitalize leading-tight">
              {timelineData.aggregation.most_common_state?.replace('_', ' ') || 'N/A'}
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      {hasData && (
        <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
          <Timeline data={timelineData.timeline} />
        </div>
      )}

      {/* Comparison View */}
      {comparisonMode && hasData && comparisonMetrics.length > 0 && (
        <div className="animate-slide-up" style={{ animationDelay: '200ms' }}>
          <ComparisonView metrics={comparisonMetrics} />
        </div>
      )}

      {/* Trend Charts */}
      {!comparisonMode && hasData && trendsData && (
        <div className="animate-slide-up" style={{ animationDelay: '200ms' }}>
          <TrendCharts
            sleepData={trendsData.sleep_quality || []}
            energyData={trendsData.energy_level || []}
            moodData={trendsData.mood || []}
          />
        </div>
      )}

      {/* HRV Trend Chart */}
      {!comparisonMode && hasData && !hrvTrendsError && hrvTrendsData && (
        <div className="animate-slide-up" style={{ animationDelay: '250ms' }}>
          <HRVTrendChart data={hrvTrendsData} />
        </div>
      )}
      {!comparisonMode && hasData && hrvTrendsError && (
        <div className="card bg-gradient-to-br from-blue-50 to-white border border-blue-200 shadow-soft animate-slide-up" style={{ animationDelay: '250ms' }}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-2xl">💓</span>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-serif font-semibold text-neutral-900 mb-2">
                HRV Trend Data Unavailable
              </h3>
              <p className="text-neutral-700 leading-relaxed">
                We need at least 3 days of HRV (Heart Rate Variability) data to show meaningful trends.
                Continue tracking your overnight HRV values in your daily check-ins to unlock this insight.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sleep Stage Trend Chart */}
      {!comparisonMode && hasData && !sleepStageTrendsError && sleepStageTrendsData && (
        <div className="animate-slide-up" style={{ animationDelay: '300ms' }}>
          <SleepStageTrendChart data={sleepStageTrendsData} />
        </div>
      )}
      {!comparisonMode && hasData && sleepStageTrendsError && (
        <div className="card bg-gradient-to-br from-purple-50 to-white border border-purple-200 shadow-soft animate-slide-up" style={{ animationDelay: '300ms' }}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-2xl">😴</span>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-serif font-semibold text-neutral-900 mb-2">
                Sleep Stage Data Unavailable
              </h3>
              <p className="text-neutral-700 leading-relaxed">
                We need at least 3 days of sleep stage data (deep, light, REM, awake) to show meaningful trends.
                Continue tracking your sleep stages in your daily check-ins to unlock this insight.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pattern Insights */}
      {patternsData && (
        <div className="animate-slide-up" style={{ animationDelay: '350ms' }}>
          <PatternInsights
            patterns={patternsData.patterns || []}
            dataPoints={patternsData.data_points || 0}
            correlations={correlationsData?.correlations || []}
            optimalRanges={optimalRangesData?.optimal_ranges || []}
          />
        </div>
      )}

      {/* Tips */}
      {hasData && (
        <div className="card bg-gradient-to-br from-primary-50 to-white border border-primary-200 shadow-soft animate-slide-up" style={{ animationDelay: '450ms' }}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-2xl">💡</span>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-serif font-semibold text-neutral-900 mb-3">
                Tips for Better Insights
              </h3>
              <ul className="text-neutral-700 space-y-2 leading-relaxed">
                <li className="flex items-start space-x-2">
                  <span className="text-primary-600 font-bold mt-0.5">•</span>
                  <span>Check in daily for the most accurate pattern detection</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary-600 font-bold mt-0.5">•</span>
                  <span>Look for trends over 2-4 weeks to identify meaningful patterns</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary-600 font-bold mt-0.5">•</span>
                  <span>Use insights to adjust your wellness routine proactively</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary-600 font-bold mt-0.5">•</span>
                  <span>Share patterns with your healthcare provider if needed</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressPage;
